import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, MenuItem, FormControl, InputLabel, Select,
  CircularProgress, Alert, InputAdornment, TableSortLabel, Chip, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useBranch } from '../../context/BranchContext';
import { getArancelPrice, getArancelBranches, IVA_RATE } from '../../utils/finance';

const CLP = (n) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 })
    .format(Math.round(Number(n) || 0));

const netoDesdeBruto = (bruto) => Math.round((Number(bruto) || 0) / (1 + IVA_RATE));
const ivaDesdeBruto = (bruto) => (Math.round(Number(bruto) || 0) - netoDesdeBruto(bruto));

const ListaPrecios = () => {
  const { isAdmin, accessibleBranches, activeBranchId } = useBranch();

  const [aranceles, setAranceles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtros
  const [filtroBranch, setFiltroBranch] = useState(activeBranchId || 'all');
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroUnidad, setFiltroUnidad] = useState('all');
  const [filtroTipo, setFiltroTipo] = useState('all');

  // Ordenamiento
  const [sortKey, setSortKey] = useState('tipoPrestacionName');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    setFiltroBranch(activeBranchId || 'all');
  }, [activeBranchId]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [arancelSnap, branchSnap, unidadSnap, tipoSnap] = await Promise.all([
          getDocs(collection(db, 'aranceles')),
          getDocs(query(collection(db, 'branches'), orderBy('name'))),
          getDocs(query(collection(db, 'unidadesNegocio'), orderBy('name'))),
          getDocs(query(collection(db, 'tiposPrestacion'), orderBy('name'))),
        ]);
        if (!mounted) return;
        setAranceles(arancelSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setBranches(branchSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setUnidades(unidadSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setTipos(tipoSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        if (mounted) setError('Error al cargar datos: ' + e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const availableBranches = isAdmin ? branches : accessibleBranches;

  // Filtrar y construir filas (una fila por arancel + sucursal con precio)
  const rows = useMemo(() => {
    const out = [];
    aranceles.forEach((a) => {
      if (a.activo === false) return;
      const arancelBranchIds = getArancelBranches(a);
      // Si el arancel no tiene branchPrices definidos, se considera disponible para todas las sucursales
      const branchIdsToShow = arancelBranchIds.length
        ? arancelBranchIds
        : availableBranches.map((b) => b.id);

      branchIdsToShow.forEach((bid) => {
        // Filtro de sucursal
        if (filtroBranch !== 'all' && bid !== filtroBranch) return;
        const price = getArancelPrice(a, bid);
        // Si el arancel tiene branchPrices y el precio es 0, puede significar que no aplica para esa sucursal
        if (Array.isArray(a.branchPrices) && a.branchPrices.length && price === 0) return;

        // Filtro por unidad de negocio
        if (filtroUnidad !== 'all' && (a.unidadNegocioId || '') !== filtroUnidad) return;
        // Filtro por tipo de prestación
        if (filtroTipo !== 'all' && (a.tipoPrestacionId || '') !== filtroTipo) return;
        // Filtro por nombre de prestación
        if (filtroNombre.trim()) {
          const q = filtroNombre.trim().toLowerCase();
          const nombre = (a.prestacion || '').toLowerCase();
          if (!nombre.includes(q)) return;
        }

        const branch = branches.find((b) => b.id === bid);
        out.push({
          id: `${a.id}_${bid}`,
          arancelId: a.id,
          prestacion: a.prestacion || '',
          tipoPrestacionName: a.tipoPrestacionName || '',
          tipoPrestacionId: a.tipoPrestacionId || '',
          unidadNegocioName: a.unidadNegocioName || '',
          unidadNegocioId: a.unidadNegocioId || '',
          branchName: branch?.name || bid,
          branchId: bid,
          total: price,
          neto: netoDesdeBruto(price),
          iva: ivaDesdeBruto(price),
        });
      });
    });
    return out;
  }, [aranceles, availableBranches, branches, filtroBranch, filtroUnidad, filtroTipo, filtroNombre]);

  // Filas ordenadas
  const sortedRows = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      let cmp = 0;
      const av = (a[sortKey] || '').toString().toLowerCase();
      const bv = (b[sortKey] || '').toString().toLowerCase();
      cmp = av.localeCompare(bv, 'es');
      if (cmp === 0) {
        // Desempate por nombre de prestación
        cmp = (a.prestacion || '').localeCompare(b.prestacion || '', 'es');
      }
      if (cmp === 0) {
        // Desempate por sucursal
        cmp = (a.branchName || '').localeCompare(b.branchName || '', 'es');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  // Agrupar por tipo de prestación para mostrar subtítulos
  const groupedRows = useMemo(() => {
    const groups = new Map();
    sortedRows.forEach((r) => {
      const key = r.tipoPrestacionName || 'Sin tipo';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    });
    return Array.from(groups.entries());
  }, [sortedRows]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const totalNeto = useMemo(() => rows.reduce((s, r) => s + r.neto, 0), [rows]);
  const totalIva = useMemo(() => rows.reduce((s, r) => s + r.iva, 0), [rows]);
  const totalBruto = useMemo(() => rows.reduce((s, r) => s + r.total, 0), [rows]);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Typography variant="h4" gutterBottom>Lista de Precios</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Aranceles activos por sucursal, unidad de negocio y tipo de prestación
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Filtros ─────────────────────────────────────────────────────── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Sucursal</InputLabel>
            <Select
              value={filtroBranch}
              label="Sucursal"
              onChange={(e) => setFiltroBranch(e.target.value)}
            >
              <MenuItem value="all">Todas las sucursales</MenuItem>
              {availableBranches.map((b) => (
                <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Unidad de Negocio</InputLabel>
            <Select
              value={filtroUnidad}
              label="Unidad de Negocio"
              onChange={(e) => setFiltroUnidad(e.target.value)}
            >
              <MenuItem value="all">Todas</MenuItem>
              {unidades.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Tipo de Prestación</InputLabel>
            <Select
              value={filtroTipo}
              label="Tipo de Prestación"
              onChange={(e) => setFiltroTipo(e.target.value)}
            >
              <MenuItem value="all">Todos</MenuItem>
              {tipos.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Buscar prestación"
            value={filtroNombre}
            onChange={(e) => setFiltroNombre(e.target.value)}
            sx={{ minWidth: 220, flex: '1 1 220px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {/* ── Tabla ──────────────────────────────────────────────────────── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortKey === 'tipoPrestacionName'}
                    direction={sortKey === 'tipoPrestacionName' ? sortDir : 'asc'}
                    onClick={() => handleSort('tipoPrestacionName')}
                  >
                    Tipo de Prestación
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortKey === 'prestacion'}
                    direction={sortKey === 'prestacion' ? sortDir : 'asc'}
                    onClick={() => handleSort('prestacion')}
                  >
                    Prestación
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortKey === 'unidadNegocioName'}
                    direction={sortKey === 'unidadNegocioName' ? sortDir : 'asc'}
                    onClick={() => handleSort('unidadNegocioName')}
                  >
                    Unidad de Negocio
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortKey === 'branchName'}
                    direction={sortKey === 'branchName' ? sortDir : 'asc'}
                    onClick={() => handleSort('branchName')}
                  >
                    Sucursal
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Valor Neto</TableCell>
                <TableCell align="right">IVA (19%)</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortKey === 'total'}
                    direction={sortKey === 'total' ? sortDir : 'asc'}
                    onClick={() => handleSort('total')}
                  >
                    Total
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No hay aranceles que coincidan con los filtros
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {groupedRows.map(([tipo, items]) => (
                <React.Fragment key={tipo}>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell colSpan={7} sx={{ fontWeight: 700, py: 1 }}>
                      <Chip
                        size="small"
                        label={`${tipo} (${items.length})`}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                  </TableRow>
                  {items.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.tipoPrestacionName || '–'}</TableCell>
                      <TableCell>{r.prestacion || '–'}</TableCell>
                      <TableCell>{r.unidadNegocioName || '–'}</TableCell>
                      <TableCell>{r.branchName || '–'}</TableCell>
                      <TableCell align="right">{CLP(r.neto)}</TableCell>
                      <TableCell align="right">{CLP(r.iva)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{CLP(r.total)}</TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Totales ────────────────────────────────────────────────────── */}
      {!loading && rows.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Paper sx={{ p: 2, minWidth: 320 }}>
            <Typography variant="subtitle2" gutterBottom>
              Totales ({rows.length} prestaciones)
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Neto:</Typography>
              <Typography variant="body2">{CLP(totalNeto)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">IVA:</Typography>
              <Typography variant="body2">{CLP(totalIva)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Total:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{CLP(totalBruto)}</Typography>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ListaPrecios;
